import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import styles from "./RepeatingTask.module.css";
import { useTranslation } from "@coworker/locales";
import Summary from "../Summary/Summary";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { Daily } from "./Daily/Daily";
import { Weekly } from "./Weekly/Weekly";
import { Monthly } from "./Monthly/Monthly";
import FixaHelperText from "@coworker/apprestructured/src/shared/wrappers/FixaHelperText/FixaHelperText";
import {
  MonthlyRecurrenceType,
  RecurrencePeriod,
  WeeklyRecurrenceDay,
} from "@coworker/apprestructured/src/task-management/types/Schedule";
import { repeatingTaskSummaryText } from "@coworker/apprestructured/src/task-management/components/ScheduleTask/helper";
import ModalColumn from "../../../ModalColumn/ModalColumn";

type FutureTaskProps = {
  start: string;
  end: string;
  startDateError: string | null;
  endDateError: string | null;
  recurrencePeriod: RecurrencePeriod | null;
  isRecurrencePeriodValid: boolean;
  monthlyInterval: string;
  weeklyInterval: string;
  weeklyRecurrenceDays: WeeklyRecurrenceDay[];
  monthlyRecurrenceType: MonthlyRecurrenceType;
  onRecurrencePeriodChange: (recurrencePeriod: RecurrencePeriod) => void;
  onStartChange: (start: string) => void;
  onEndChange: (end: string) => void;
  onMonthlyIntervalChange: (interval: string) => void;
  onMonthlyRecurrenceTypeChange: (type: MonthlyRecurrenceType) => void;
  onWeeklyIntervalChange: (interval: string) => void;
  onWeeklyRecurrenceDaysChange: (days: WeeklyRecurrenceDay[]) => void;
  weeklyIntervalError: string | null;
  monthlyIntervalError: string | null;
};

export const RepeatingTask = (props: FutureTaskProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div>
        <FixaText textStyle="Heading.S">{t("repeatString")}</FixaText>
        <FixaListView id="repeatTaskOptions" size="small">
          <FixaListViewItem
            title={t("dailyString")}
            control="radiobutton"
            onChange={() => {
              props.onRecurrencePeriodChange("daily");
            }}
            controlProps={{
              checked: props.recurrencePeriod === "daily",
              value: "daily",
            }}
          />
          {props.recurrencePeriod === "daily" && (
            <ModalColumn gap="medium" className={styles["topBottomPadding"]}>
              <Daily
                start={props.start}
                end={props.end}
                startDateError={props.startDateError}
                endDateError={props.endDateError}
                onStartChange={props.onStartChange}
                onEndChange={props.onEndChange}
              />
            </ModalColumn>
          )}
          <FixaListViewItem
            title={t("weeklyString")}
            control="radiobutton"
            onChange={() => props.onRecurrencePeriodChange("weekly")}
            controlProps={{
              checked: props.recurrencePeriod === "weekly",
              value: "weekly",
            }}
          />
          {props.recurrencePeriod === "weekly" && (
            <ModalColumn gap="medium" className={styles["topBottomPadding"]}>
              <Weekly
                start={props.start}
                end={props.end}
                startDateError={props.startDateError}
                endDateError={props.endDateError}
                interval={props.weeklyInterval}
                days={props.weeklyRecurrenceDays}
                onStartChange={props.onStartChange}
                onEndChange={props.onEndChange}
                onIntervalChange={props.onWeeklyIntervalChange}
                onDaysChange={props.onWeeklyRecurrenceDaysChange}
                intervalError={props.weeklyIntervalError}
              />
            </ModalColumn>
          )}
          <FixaListViewItem
            title={t("monthlyString")}
            control="radiobutton"
            onChange={() => {
              props.onRecurrencePeriodChange("monthly");
            }}
            controlProps={{
              checked: props.recurrencePeriod === "monthly",
              value: "monthly",
            }}
          />
          {props.recurrencePeriod === "monthly" && (
            <ModalColumn gap="medium" className={styles["topPadding"]}>
              <Monthly
                start={props.start}
                end={props.end}
                startDateError={props.startDateError}
                endDateError={props.endDateError}
                interval={props.monthlyInterval}
                type={props.monthlyRecurrenceType}
                onStartChange={props.onStartChange}
                onEndChange={props.onEndChange}
                onIntervalChange={props.onMonthlyIntervalChange}
                onTypeChange={props.onMonthlyRecurrenceTypeChange}
                intervalError={props.monthlyIntervalError}
              />
            </ModalColumn>
          )}
        </FixaListView>
        {!props.isRecurrencePeriodValid && (
          <FixaHelperText
            shouldValidate
            children={t("pleaseSelectOptionToContinueString")}
            className={styles["helperText"]}
          />
        )}
      </div>
      {props.start && props.end && (
        <Summary>
          {repeatingTaskSummaryText(
            props.recurrencePeriod,
            props.start,
            props.end,
            props.weeklyInterval,
            props.weeklyRecurrenceDays,
            props.monthlyInterval,
            props.monthlyRecurrenceType,
            t
          )}
        </Summary>
      )}
    </>
  );
};
